import {env} from '../config/env.js';
import {authService} from './auth.service.js';
import {triggerSessionExpired} from './session-expired.js';

export type FetchAudience = 'morphed' | 'clam' | 'auto';

export interface AuthedFetchOptions extends RequestInit {
    audience?: FetchAudience;
    skipAuthRefresh?: boolean;
}

let refreshInFlight: Promise<boolean> | null = null;

function refreshOnce(): Promise<boolean> {
    if (refreshInFlight) return refreshInFlight;
    const p = Promise.race([
        authService.refreshSession(),
        new Promise<boolean>((res) => setTimeout(() => res(false), 5000)),
    ]);
    refreshInFlight = p.finally(() => { refreshInFlight = null; });
    return refreshInFlight;
}

function resolveAudience(input: string, audience: FetchAudience | undefined): 'morphed' | 'clam' | 'none' {
    if (audience === 'morphed') return 'morphed';
    if (audience === 'clam') return 'clam';
    const isMorphed = input.startsWith(env.MORPHED_API_URL) || input.startsWith('/api');
    if (isMorphed) return 'morphed';
    const isClam = !!env.CLAM_BACKEND_URL && input.startsWith(env.CLAM_BACKEND_URL);
    if (isClam) return 'clam';
    return 'none';
}

function applyAuth(target: 'morphed' | 'clam' | 'none', headers: Headers): void {
    if (target === 'morphed') {
        const idToken = localStorage.getItem('idToken');
        if (idToken) {
            headers.set('Authorization', `Bearer ${idToken}`);
        } else if (env.MORPHED_API_KEY) {
            headers.set('X-Morphed-Key', env.MORPHED_API_KEY);
        }
    } else if (target === 'clam') {
        const idToken = localStorage.getItem('idToken');
        if (idToken) headers.set('Authorization', `Bearer ${idToken}`);
    }
}

export async function authedFetch(input: string, options: AuthedFetchOptions = {}): Promise<Response> {
    const {audience, skipAuthRefresh, headers, ...rest} = options;
    const target = resolveAudience(input, audience);

    const finalHeaders = new Headers(headers);
    if (target !== 'none' && !finalHeaders.has('Content-Type')) {
        finalHeaders.set('Content-Type', 'application/json');
    }

    applyAuth(target, finalHeaders);

    let response = await fetch(input, {...rest, headers: finalHeaders});

    if (response.status === 401 && target !== 'none' && !skipAuthRefresh) {
        const hadIdToken = !!localStorage.getItem('idToken');
        if (hadIdToken) {
            try {
                const ok = await refreshOnce();
                if (ok) {
                    applyAuth(target, finalHeaders);
                    response = await fetch(input, {...rest, headers: finalHeaders});
                } else if (target === 'morphed' && env.MORPHED_API_KEY) {
                    finalHeaders.delete('Authorization');
                    finalHeaders.set('X-Morphed-Key', env.MORPHED_API_KEY);
                    response = await fetch(input, {...rest, headers: finalHeaders});
                }
            } catch {
                if (target === 'morphed' && env.MORPHED_API_KEY) {
                    finalHeaders.delete('Authorization');
                    finalHeaders.set('X-Morphed-Key', env.MORPHED_API_KEY);
                    response = await fetch(input, {...rest, headers: finalHeaders});
                }
            }
        }
        // Sigue 401 tras intentar recuperar (refresh falló y sin fallback de API key):
        // la sesión expiró realmente → redirigir a /sesion-expirada para no dejar el sitio trabado.
        if (response.status === 401 && hadIdToken) {
            triggerSessionExpired();
        }
    }

    return response;
}
